import React, { FC, useState } from 'react';
import { BUTTON_TYPE, ButtonComponent } from '../ButtonComponent';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useLocation } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons';
import { Dropdown } from 'rsuite';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { DisputeForm } from '../users/DisputeForm';
import { BlockListModal } from './BlockListModal';
import { CancelOrderModal } from './CancelOrderModal';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { fetchOrderDetailsData } from '../../store/order/actions/order';
import { useOrdersContext } from '../../store/order/order-context';

import './AdditionalHeaderButtons.scss';
import clsx from 'clsx';
import { IOrder, IOrderHateoasLinks } from './OrderDetails';

interface IOrderHeaderDropdownItem {
	key: string;
	name: string;
	roles: CLAIMS;
	isDisabled: boolean;
}
const getDropdownList = (orderData: IOrder, isDisputeAction: boolean ): IOrderHeaderDropdownItem[] => [
	{
		key: 'blockList',
		name: 'Add to blocklist',
		roles: CLAIMS.SUPER_ADMIN_AND_MERCHANT_ADMIN_AND_PARTNER_ADMIN,
		isDisabled: false

	},
	{
		key: 'cancel',
		name: 'Cancel order',
		roles: CLAIMS.SUPER_ADMIN_AND_MERCHANT_ADMIN_AND_PARTNER_ADMIN_AND_MERCHANT_SUPPORT_ADMIN,
		isDisabled: !orderData?.isCancelable
	},
	{
		key: 'dispute',
		name: 'Dispute',
		roles: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN,
		isDisabled: isDisputeAction
	},
];

interface Props {
	actionLinks: IOrderHateoasLinks[];
}

export const AdditionalHeaderButtons: FC<Props> = ({ actionLinks }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const searchParams = new URLSearchParams(search);
	const { id } = useParams<{ id: string; type: string }>();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const { orderContextState: { orderDetailsData: order }, dispatch } = useOrdersContext();

	const [isCancelOrderModalOpen, setIsCancelOrderModalOpen] = useState<boolean>(false);
	const [isBlockListModalOpen, setIsBlockListModalOpen] = useState<boolean>(false);
	const [isDisputeModalOpen, setIsDisputeModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const disputeAction = actionLinks?.find(link => link?.actionName?.toLowerCase() === 'dispute' && link?.category?.toLowerCase() === 'orders');

	const dropdownItems = getDropdownList(order, !disputeAction)?.filter(item => checkRoles(authUser, item.roles));

	const handleDropdownClick = (key: string): void => {
		switch (key) {
			case 'cancel':
				setIsCancelOrderModalOpen(true);
				break;
			case 'blockList':
				setIsBlockListModalOpen(true);
				break;
			case 'dispute':
				setIsDisputeModalOpen(true);
				break;
		}
	};

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const handleSuccess = (): void => {
		setIsDisputeModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: 'Dispute information',
			content: (
				<div className="fs-6">
					Dispute information was successfully saved!
				</div>
			),
			confirmButtonText: t('PARTNERS.SUCCESS_BTN_NAME'),
			minHeight: 100,
			onConfirm: async () => {
				closeModal();
				await fetchOrderDetailsData(dispatch, id);
			},
			onClose: closeModal
		});
	};

	const handleError = (): void => {
		setIsDisputeModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: 'Dispute information',
			content: (
				<div className="fs-6 text-danger">
					Failed adding dispute information
				</div>
			),
			confirmButtonText: t('PARTNERS.FAIL_BTN_NAME'),
			minHeight: 100,
			onConfirm: () => {
				setModalState(prev => ({ ...prev, isOpen: false }));
				setIsDisputeModalOpen(true);
			},
			onClose: closeModal
		});
	};

	return (
		<>
			{
				checkRoles(authUser, CLAIMS.SUPER_ADMIN) && (
					<ButtonComponent
						buttonType={BUTTON_TYPE.WHITE}
						buttonName={t('ORDERS.ACTIVITY_LOG_BTN_NAME')}
						additionalClass="d-none d-md-block"
						onButtonClick={(): void => {
							let path = `${pathname}/activity`;
							if (searchParams.has('isGhostMode')) {
								path += '?isGhostMode=true';
							}
							navigate(path);
						}}
					/>
				)
			}

			{
				dropdownItems.map(({ key, name, isDisabled }) => (
					<ButtonComponent
						buttonName={name}
						key={key}
						additionalClass="ms-5 order-header-buttons"
						buttonType={BUTTON_TYPE.WHITE}
						onButtonClick={(): void => handleDropdownClick(key)}
						isDisabled={isDisabled}
					/>
				))
			}

			{
				checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_MERCHANT_ADMIN_AND_PARTNER_ADMIN_AND_MERCHANT_SUPPORT_ADMIN) && (
					<Dropdown
						noCaret
						icon={<FontAwesomeIcon icon={faEllipsisVertical} className="order-dropdown"/>}
						placement="leftStart"
						className="order-dropdown ms-5"
					>
						{
							dropdownItems.map(({ key, name, isDisabled }) => (
								<Dropdown.Item
									key={key}
									onClick={(): void => handleDropdownClick(key)}
									className={clsx('fw-bolder fs-7', isDisabled && 'text-muted')}
									disabled={isDisabled}
								>
									{name}
								</Dropdown.Item>
							))
						}
					</Dropdown>
				)
			}


			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

			<ModalWindow
				isOpen={isDisputeModalOpen}
				modalTitle='Dispute information'
				onClose={(): void => setIsDisputeModalOpen(false)}
				contentClassName="d-block"
				backdrop="static"
				enableOverflow={false}
				content={
					<DisputeForm
						closeModal={(): void => setIsDisputeModalOpen(false)}
						onSuccess={handleSuccess}
						onError={handleError}
						orderId={id}
						disputeAction={disputeAction}
					/>
				}
			/>

			<ModalWindow
				isOpen={isBlockListModalOpen}
				modalTitle='Add to blocklist'
				onClose={(): void => setIsBlockListModalOpen(false)}
				contentClassName="d-block"
				backdrop="static"
				enableOverflow={false}
				content={
					<BlockListModal
						closeModal={(): void => setIsBlockListModalOpen(false)}
						orderId={id}
					/>
				}
			/>

			<ModalWindow
				isOpen={isCancelOrderModalOpen}
				modalTitle='Cancel Order'
				onClose={(): void => setIsCancelOrderModalOpen(false)}
				contentClassName="d-block"
				minHeight={100}
				backdrop="static"
				content={
					<CancelOrderModal
						closeModal={(): void => setIsCancelOrderModalOpen(false)}
						orderId={id}
					/>
				}
			/>
		</>
	);
};
